import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { PageHero } from "@/components/site/PageHero";
import assessmentHeroSrc from "@/assets/assessment-hero.jpg";
const assessmentHero = { url: assessmentHeroSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { behaviors, dimensions, dimensionList } from "@/content/behaviors";
import type { DimensionSlug } from "@/content/behaviors";
import { SITE_URL } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { saveAssessmentLead } from "@/utils/assessment-leads.functions";

export const Route = createFileRoute("/leadership-gaps-assessment")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/leadership-gaps-assessment/` }],
    meta: [
      { title: "Leadership Gaps Assessment | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Sixteen questions, about five minutes. Find out which of the four leadership dimensions your managers are thinnest in, and which courses address it.",
      },
      { property: "og:title", content: "Leadership Gaps Assessment | Leadership Edge Live" },
      {
        property: "og:description",
        content:
          "Find out which leadership behaviors your managers are missing, and what to do about it.",
      },
    ],
  }),
  component: LeadershipGapsAssessment,
});

type Response = number | null;

const responseLabels = [
  "Rarely demonstrated",
  "Occasionally demonstrated",
  "Demonstrated by some leaders",
  "Demonstrated by most leaders",
  "Consistently demonstrated",
];

function LeadershipGapsAssessment() {
  const [phase, setPhase] = useState<"intro" | "assess" | "contact" | "results">("intro");
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState<Response[]>(() =>
    new Array(behaviors.length).fill(null),
  );
  const [current, setCurrent] = useState(0);
  const [reviewed, setReviewed] = useState<boolean[]>(() =>
    new Array(behaviors.length).fill(false),
  );

  const scores = useMemo(
    () => responses.map((r) => (r == null ? null : ((r - 1) / 4) * 100)),
    [responses],
  );
  const answeredCount = responses.filter((r) => r != null).length;
  const allAnswered = answeredCount === behaviors.length;

  const dimensionScores = useMemo(() => {
    const map: Record<DimensionSlug, number | null> = {
      "leads-self": null,
      "understands-and-adapts": null,
      "delivers-results": null,
      "mobilizes-people": null,
    };
    dimensionList.forEach((d) => {
      const relevant = behaviors
        .map((b, i) => ({ b, s: scores[i] }))
        .filter(({ b, s }) => b.dimension === d && s != null);
      if (relevant.length >= 3) {
        map[d] = relevant.reduce((sum, r) => sum + (r.s as number), 0) / relevant.length;
      }
    });
    return map;
  }, [scores]);

  const overall = useMemo(() => {
    const valid = scores.filter((s) => s != null) as number[];
    if (valid.length < 14) return null;
    return valid.reduce((a, b) => a + b, 0) / valid.length;
  }, [scores]);

  const setResponse = (v: number | null) => {
    setResponses((prev) => {
      const next = [...prev];
      next[current] = v;
      return next;
    });
    setReviewed((prev) => {
      const next = [...prev];
      next[current] = true;
      return next;
    });
    if (current < behaviors.length - 1) {
      setTimeout(() => setCurrent(current + 1), 150);
    }
  };

  if (phase === "intro") {
    return (
      <>
        <section className="relative isolate overflow-hidden">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <img
              src={assessmentHero.url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
              }}
            />
          </div>
          <div className="relative mx-auto flex min-h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
            <div className="max-w-[600px]">
              <h1
                className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Discover your managers' gaps
              </h1>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
                Sixteen questions, about five minutes. You'll get back which of the four leadership
                dimensions your managers are weakest in, which behaviors are strongest, and the
                specific courses that address the gaps.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => setPhase("assess")}
                  className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
                >
                  Start the assessment
                </button>
                <Link
                  to="/contact/"
                  className="inline-flex items-center justify-center rounded-[6px] border border-black/40 bg-transparent px-6 py-3 text-sm font-semibold text-black transition-colors hover:border-black/60 hover:bg-black/5"
                >
                  Talk to a Development Pro
                </Link>
              </div>
            </div>
          </div>
        </section>
        <Section tone="default">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
            <div>
              <h2
                className="text-3xl font-semibold leading-tight text-ink md:text-4xl"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                What you get back
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                {[
                  "Which of the four dimensions is thinnest",
                  "A score for all 16 leader behaviors",
                  "Your three strongest behaviors",
                  "Your three biggest gaps",
                  "The courses that address those gaps",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <span
                      className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-[#693D94]"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-muted-foreground">
                About 5 minutes · 16 questions · Results on screen. This is a planning tool, not a
                validated psychological assessment.
              </p>
            </div>
            <div className="rounded-2xl border border-primary/20 bg-lavender p-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                Before you start
              </p>
              <h3 className="mt-2 font-display text-2xl font-semibold text-ink">
                Rate what actually happens, not what's intended
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Think about one clearly defined group of leaders. For each statement, pick what most
                employees would say they experience today. Give honest answers. You're trying to
                find your true gaps, not get a high score.
              </p>
              <button
                onClick={() => setPhase("assess")}
                className="mt-6 inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand"
              >
                Start the assessment
              </button>
            </div>
          </div>
        </Section>
      </>
    );
  }

  if (phase === "assess") {
    const b = behaviors[current];
    const progress = ((current + 1) / behaviors.length) * 100;
    return (
      <div className="flex min-h-[calc(100vh-5rem)] items-center bg-lavender py-6 md:py-8">
        <div className="mx-auto w-full max-w-2xl px-6">
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-primary">
              <span>
                Question {current + 1} of {behaviors.length}
              </span>
              <span>{b.dimension.replace(/-/g, " ")}</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background">
              <div
                className="h-full bg-[#693D94] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <div className="rounded-3xl border border-primary/20 bg-background p-5 sm:p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">{b.name}</p>
            <h2 className="mt-2 font-display text-lg leading-snug text-ink sm:text-xl md:text-2xl">
              {b.statement}
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
              {b.definition}
            </p>
            <div className="mt-4 space-y-1.5">
              {responseLabels.map((label, i) => {
                const value = i + 1;
                const selected = responses[current] === value;
                return (
                  <button
                    key={label}
                    onClick={() => setResponse(value)}
                    className={`w-full rounded-xl border px-4 py-2.5 text-left text-sm transition-all ${
                      selected
                        ? "border-primary bg-lavender text-ink shadow-brand"
                        : "border-border bg-background text-foreground hover:border-primary/40 hover:bg-lavender/50"
                    }`}
                  >
                    <span className="font-semibold text-primary">{value}.</span> {label}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex items-center justify-between gap-4">
              <button
                onClick={() => setCurrent(Math.max(0, current - 1))}
                disabled={current === 0}
                className="text-sm font-semibold text-muted-foreground hover:text-primary disabled:opacity-40"
              >
                ← Previous
              </button>
              <div className="flex items-center gap-4">
                {allAnswered ? (
                  <button
                    onClick={() => setPhase("contact")}
                    className="inline-flex items-center rounded-[6px] bg-[#693D94] px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
                  >
                    See your results
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {answeredCount} of {behaviors.length} answered
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "contact") {
    const handleContact = async (e: React.FormEvent) => {
      e.preventDefault();
      setSaving(true);
      const stageName =
        overall == null
          ? "Leadership visibility needs attention"
          : overall < 40
            ? "Leader-Dependent"
            : overall < 70
              ? "Building Consistency"
              : "Ready to Scale";
      const topGaps = behaviors
        .map((b, i) => ({ name: b.name, s: scores[i] }))
        .filter((x) => x.s != null)
        .sort((a, b) => (a.s as number) - (b.s as number))
        .slice(0, 3)
        .map((x) => x.name);
      const scoredList = behaviors
        .map((b, i) => ({ b, score: scores[i] }))
        .filter((x) => x.score != null) as { b: (typeof behaviors)[number]; score: number }[];
      const ranked = [...scoredList].sort((a, b) => b.score - a.score);
      const stageCopy = stageFor(overall);
      let pdfBase64: string | undefined;
      try {
        const { assessmentPdfBase64 } = await import("@/lib/assessment-pdf");
        pdfBase64 = await assessmentPdfBase64({
          stageName: stageCopy.name,
          stageHeadline: stageCopy.headline,
          stageBody: stageCopy.body,
          overall,
          dimensionScores,
          scores,
          strengths: ranked.slice(0, 3).map(({ b, score }) => ({ name: b.name, score })),
          priorities: ranked
            .slice(-3)
            .reverse()
            .map(({ b, score }) => ({
              name: b.name,
              score,
              resultIntro: b.resultIntro,
              courses: b.curatedCourses.map((c) => c.title),
            })),
          recipientName: leadName.trim(),
        });
      } catch {
        pdfBase64 = undefined;
      }
      try {
        await saveAssessmentLead({
          data: {
            name: leadName,
            email: leadEmail,
            stage: stageName,
            overall,
            dimensions: dimensionList.map((d) => dimensionScores[d]),
            topGaps,
            scores,
            pdfBase64,
          },
        });
      } catch {
        // Saving should never block someone from seeing their results.
      } finally {
        setSaving(false);
        setPhase("results");
      }
    };

    return (
      <div className="min-h-[calc(100vh-4rem)] bg-lavender px-6 py-16 md:py-24">
        <form
          onSubmit={handleContact}
          className="mx-auto max-w-md rounded-2xl border border-border/60 bg-background p-7 shadow-brand"
        >
          <h1
            className="text-2xl font-semibold leading-tight text-ink md:text-3xl"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Your results are ready
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Tell us where to reach you. A Development Pro may follow up once to talk through your
            results.
          </p>
          <div className="mt-6 space-y-5">
            <div>
              <Label htmlFor="lead-name" className="text-sm font-semibold text-ink">
                Your name
              </Label>
              <Input
                id="lead-name"
                required
                maxLength={120}
                autoComplete="name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="lead-email" className="text-sm font-semibold text-ink">
                Work email
              </Label>
              <Input
                id="lead-email"
                type="email"
                required
                maxLength={255}
                autoComplete="email"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="mt-7 inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand disabled:opacity-60"
          >
            {saving ? "Opening your results..." : "Show my results"}
          </button>
          <button
            type="button"
            onClick={() => setPhase("results")}
            className="mt-3 inline-flex w-full items-center justify-center rounded-[6px] border border-primary/40 px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-lavender"
          >
            Skip email and show my results
          </button>
          <button
            type="button"
            onClick={() => setPhase("assess")}
            className="mt-4 w-full text-sm font-semibold text-muted-foreground hover:text-primary"
          >
            Back to the questions
          </button>
        </form>
      </div>
    );
  }

  return (
    <Results
      overall={overall}
      dimensionScores={dimensionScores}
      scores={scores}
      hasContact={leadEmail.trim().length > 0}
      onRestart={() => {
        setResponses(new Array(behaviors.length).fill(null));
        setReviewed(new Array(behaviors.length).fill(false));
        setCurrent(0);
        setPhase("intro");
      }}
    />
  );
}

function stageFor(overall: number | null) {
  return overall == null
    ? {
        name: "Leadership visibility needs attention",
        headline: "Too many behaviors were skipped to score this",
        body: "You marked too many as not visible. Ask someone closer to those teams to take it too, then compare what you each saw.",
      }
    : overall < 40
      ? {
          name: "Leader-Dependent",
          headline: "Right now, a good experience depends on which manager you got",
          body: "The behaviors aren't consistent yet. Two employees doing the same job can have completely different experiences depending on who they report to.",
        }
      : overall < 70
        ? {
            name: "Building Consistency",
            headline: "Some of your managers do this well. Not all of them yet.",
            body: "The good behaviors are there, just not everywhere. The gap between your strongest and weakest managers is where the work is.",
          }
        : {
            name: "Ready to Scale",
            headline: "Your managers are consistent enough to build on",
            body: "Most leaders show these behaviors most of the time. Protect that, and spend your effort on the two or three specific gaps below.",
          };
}

function Results({
  overall,
  dimensionScores,
  scores,
  onRestart,
  hasContact,
}: {
  overall: number | null;
  dimensionScores: Record<DimensionSlug, number | null>;
  scores: (number | null)[];
  onRestart: () => void;
  hasContact: boolean;
}) {
  const stage = useMemo(() => stageFor(overall), [overall]);

  const scoredBehaviors = behaviors
    .map((b, i) => ({ b, score: scores[i] }))
    .filter((x) => x.score != null) as { b: (typeof behaviors)[number]; score: number }[];

  const sorted = [...scoredBehaviors].sort((a, b) => b.score - a.score);
  const strengths = sorted.slice(0, 3);
  const priorities = sorted.slice(-3).reverse();

  const lowestDimension = dimensionList
    .filter((d) => dimensionScores[d] != null)
    .sort((a, b) => (dimensionScores[a] as number) - (dimensionScores[b] as number))[0];

  const [downloading, setDownloading] = useState(false);

  const pdfInput = useMemo(
    () => ({
      stageName: stage.name,
      stageHeadline: stage.headline,
      stageBody: stage.body,
      overall,
      dimensionScores,
      scores,
      strengths: strengths.map(({ b, score }) => ({ name: b.name, score })),
      priorities: priorities.map(({ b, score }) => ({
        name: b.name,
        score,
        resultIntro: b.resultIntro,
        courses: b.curatedCourses.map((c) => c.title),
      })),
    }),
    [stage, overall, dimensionScores, scores, strengths, priorities],
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { downloadAssessmentPdf } = await import("@/lib/assessment-pdf");
      await downloadAssessmentPdf(pdfInput);
      setDownloaded(true);
    } finally {
      setDownloading(false);
    }
  };

  const [downloaded, setDownloaded] = useState(false);
  const [followName, setFollowName] = useState("");
  const [followEmail, setFollowEmail] = useState("");
  const [followSaving, setFollowSaving] = useState(false);
  const [followSent, setFollowSent] = useState(false);

  const handleFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setFollowSaving(true);
    let pdfBase64: string | undefined;
    try {
      const { assessmentPdfBase64 } = await import("@/lib/assessment-pdf");
      pdfBase64 = await assessmentPdfBase64({ ...pdfInput, recipientName: followName.trim() });
    } catch {
      pdfBase64 = undefined;
    }
    try {
      await saveAssessmentLead({
        data: {
          name: followName,
          email: followEmail,
          stage: stage.name,
          overall,
          dimensions: dimensionList.map((d) => dimensionScores[d]),
          topGaps: priorities.map(({ b }) => b.name),
          scores,
          pdfBase64,
        },
      });
    } catch {
      // Never block the visitor; they already have their PDF.
    } finally {
      setFollowSaving(false);
      setFollowSent(true);
    }
  };

  // Prepare the PDF quietly, so a visitor who clicks through to the contact
  // form can have their results attached to the message.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ assessmentPdfBase64 }, { storeAssessmentPdf }] = await Promise.all([
        import("@/lib/assessment-pdf"),
        import("@/lib/assessment-handoff"),
      ]);
      const base64 = await assessmentPdfBase64(pdfInput);
      if (!cancelled) storeAssessmentPdf(base64);
    })().catch(() => {
      // If it can't be prepared, the contact form just sends no attachment.
    });
    return () => {
      cancelled = true;
    };
  }, [pdfInput]);

  const handleAdvisorHandoff = async () => {
    const { markAssessmentHandoff } = await import("@/lib/assessment-handoff");
    markAssessmentHandoff();
  };

  return (
    <>
      <PageHero maxWidth="6xl" headline={<>{stage.headline}</>} supporting={stage.body} />

      <Section tone="default">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr]">
          <div className="rounded-3xl border border-primary/20 bg-lavender p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Overall stage
            </p>
            <p className="mt-2 font-display text-3xl text-ink">{stage.name}</p>
            {overall != null && (
              <>
                <p className="mt-4 text-xs text-muted-foreground">Consistency score</p>
                <p className="font-display text-5xl text-primary">
                  {Math.round(overall)}
                  <span className="text-2xl text-muted-foreground">/100</span>
                </p>
              </>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="mt-6 inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand disabled:opacity-60"
            >
              {downloading ? "Preparing PDF..." : "Download results as PDF"}
            </button>
            {downloaded && !hasContact && (
              <div className="mt-5 rounded-xl border border-primary/20 bg-lavender/60 p-4 text-left">
                {followSent ? (
                  <p className="text-sm text-ink">
                    Thanks. Your results are on the way, and a Development Pro may reach out once.
                  </p>
                ) : (
                  <form onSubmit={handleFollowUp} className="space-y-3">
                    <p className="text-sm font-semibold text-ink">Want more detail?</p>
                    <p className="text-xs text-muted-foreground">
                      Add your email and we'll send your results. A Development Pro can walk you
                      through them.
                    </p>
                    <Input
                      required
                      maxLength={120}
                      placeholder="Your name"
                      aria-label="Your name"
                      autoComplete="name"
                      value={followName}
                      onChange={(e) => setFollowName(e.target.value)}
                    />
                    <Input
                      required
                      type="email"
                      maxLength={255}
                      placeholder="Work email"
                      aria-label="Work email"
                      autoComplete="email"
                      value={followEmail}
                      onChange={(e) => setFollowEmail(e.target.value)}
                    />
                    <button
                      type="submit"
                      disabled={followSaving}
                      className="inline-flex w-full items-center justify-center rounded-[6px] border border-primary/40 bg-background px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-lavender disabled:opacity-60"
                    >
                      {followSaving ? "Sending..." : "Submit"}
                    </button>
                  </form>
                )}
              </div>
            )}
            <button
              onClick={onRestart}
              className="mt-4 text-xs font-semibold uppercase tracking-wider text-primary hover:underline"
            >
              Start over
            </button>
          </div>
          <div>
            <h3 className="font-display text-xl text-ink">How the four dimensions compare</h3>
            <div className="mt-4 space-y-4">
              {dimensionList.map((d) => {
                const s = dimensionScores[d];
                return (
                  <div key={d}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-ink">{dimensions[d].name}</span>
                      <span className="text-muted-foreground">
                        {s == null ? "Not enough answers" : `${Math.round(s)}`}
                      </span>
                    </div>
                    <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-lavender-deep">
                      <div
                        className="h-full bg-[#693D94] transition-all"
                        style={{ width: s == null ? "0%" : `${s}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      <Section tone="lavender">
        <SectionHeader title="What's working and what isn't" />
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Strongest behaviors
            </p>
            <ul className="mt-3 space-y-3">
              {strengths.map(({ b, score }) => (
                <li key={b.slug} className="rounded-xl border border-border/60 bg-background p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-lg text-ink">{b.name}</span>
                    <span className="text-sm font-semibold text-primary">{Math.round(score)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{b.definition}</p>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Biggest gaps
            </p>
            <ul className="mt-3 space-y-3">
              {priorities.map(({ b, score }) => (
                <li key={b.slug} className="rounded-xl border border-primary/20 bg-background p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-lg text-ink">{b.name}</span>
                    <span className="text-sm font-semibold text-primary">{Math.round(score)}</span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{b.resultIntro}</p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-primary">
                    Courses that address this
                  </p>
                  <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                    {b.curatedCourses.map((c) => (
                      <li key={c.url}>
                        <Link to={c.url} className="hover:text-primary">
                          → {c.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {lowestDimension && (
        <Section tone="default">
          <SectionHeader
            eyebrow="Where to start"
            title={dimensions[lowestDimension].heading}
            intro={dimensions[lowestDimension].interpretation}
          />
          <ol className="grid gap-4 md:grid-cols-3">
            {dimensions[lowestDimension].actions.map((a, i) => (
              <li key={a} className="rounded-2xl border border-border/60 bg-background p-6">
                <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#693D94] font-display text-sm text-primary-foreground">
                  {i + 1}
                </div>
                <p className="text-sm text-ink">{a}</p>
              </li>
            ))}
          </ol>
        </Section>
      )}

      <Section tone="lavender">
        <SectionHeader title="All 16 behaviors" />
        <div className="grid gap-3 md:grid-cols-2">
          {behaviors.map((b, i) => {
            const s = scores[i];
            return (
              <div
                key={b.slug}
                className="flex items-center gap-4 rounded-xl border border-border/60 bg-background p-4"
              >
                <span className="font-display text-lg text-primary">{b.number}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-ink">{b.name}</p>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-lavender-deep">
                    <div
                      className="h-full bg-[#693D94]"
                      style={{ width: s == null ? "0%" : `${s}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {s == null ? "N/A" : Math.round(s)}
                </span>
              </div>
            );
          })}
        </div>
      </Section>

      <AdvisorCTA
        title="Want a second read on these results?"
        body="A Development Pro will go through your results with you, suggest which gap to start with, and tell you plainly if training isn't the answer."
        backgroundImage={boardroomAsset.url}
        onSecondaryClick={handleAdvisorHandoff}
      />
    </>
  );
}
